/**
 * Shared infrastructure for Portal API hooks.
 *
 * `portalFetch` / `portalFetchCamel` are the only HTTP entrypoints; every
 * per-resource hook file imports from here. `portalKeys` lives here because
 * mutations across resources invalidate each other (e.g. project mutations
 * also invalidate dashboard + usage), so a single key factory is required.
 */

import { getAuthTokenAsync } from '@/services/auth/token-manager';
import { config } from '@/utils/env';
import { getCurrentOrgId, getIsImpersonating } from '@/contexts/OrgContext';
import { transformKeysToCamelCase } from '@/utils/transform-keys';
import { APIError } from '../../../../../shared/utils/api-helpers';

// ============================================================================
// Fetch Helpers
// ============================================================================

/**
 * Parse a non-OK portal response into an APIError (message + optional code +
 * status). Shared by portalFetch and portalUpload.
 *
 * MUST throw APIError, not a plain Error (CUR-1369): the global
 * QueryCache.onError handler in services/query/query-client.ts branches on
 * `error instanceof APIError` to turn a 401 into clearAllAuthState() plus a
 * redirect. A plain Error left expired-token portal users stuck on an
 * endless stream of failed queries instead of being logged out.
 */
async function throwPortalError(response: Response): Promise<never> {
  const errorBody = await response.json().catch(() => ({}));
  const detail = errorBody.detail;
  let message: string;
  let code: string | undefined;
  if (typeof detail === 'string') {
    message = detail;
  } else if (Array.isArray(detail)) {
    message = detail
      .map((e: { msg?: string; message?: string }) => e.msg || e.message || JSON.stringify(e))
      .join('; ');
  } else if (detail && typeof detail === 'object') {
    // Structured errors surface a { error, message } body — render the message,
    // preserve the code so onboarding can show a "Resend verification" affordance.
    if (typeof detail.error === 'string') code = detail.error;
    message = typeof detail.message === 'string' ? detail.message : `Portal API error: ${response.status}`;
  } else {
    message = `Portal API error: ${response.status}`;
  }
  // APIError carries .status and .code, so existing consumers that read
  // those fields off the thrown error (e.g. onboarding's resend-verification
  // affordance) keep working unchanged.
  throw new APIError(response.status, message, code);
}

/** Auth + org-impersonation headers shared by every portal request. */
async function portalAuthHeaders(): Promise<Record<string, string>> {
  const token = await getAuthTokenAsync();
  // Super admins impersonating an org send the selected org ID as a header.
  // Regular portal users rely on their JWT for org scoping — no header needed.
  const orgId = getCurrentOrgId();
  const isImpersonating = getIsImpersonating();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(isImpersonating && orgId ? { 'X-Organization-ID': orgId } : {}),
  };
}

export async function portalFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const url = `${config.apiUrl}${path}`;

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(await portalAuthHeaders()),
      ...options.headers,
    },
  });

  if (!response.ok) {
    await throwPortalError(response);
  }

  return response.json();
}

/**
 * Upload multipart/form-data to a portal endpoint.
 *
 * Distinct from portalFetch because the browser must set the
 * `multipart/form-data` Content-Type (with boundary) itself — we must NOT
 * send `application/json`, so this helper omits the Content-Type header
 * entirely.
 */
export async function portalUpload<T>(path: string, formData: FormData): Promise<T> {
  const url = `${config.apiUrl}${path}`;

  const response = await fetch(url, {
    method: 'POST',
    body: formData,
    headers: await portalAuthHeaders(),
  });

  if (!response.ok) {
    await throwPortalError(response);
  }

  return response.json();
}

/** Download a portal file response, named by its Content-Disposition. */
export async function portalDownload(path: string, fallbackName: string): Promise<void> {
  const response = await fetch(`${config.apiUrl}${path}`, { headers: await portalAuthHeaders() });
  if (!response.ok) {
    await throwPortalError(response);
  }
  const blob = await response.blob();
  const name = response.headers.get('Content-Disposition')?.match(/filename="?([^";\n]+)"?/)?.[1] ?? fallbackName;
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

/**
 * Fetch from portal API and transform snake_case keys to camelCase.
 */
export async function portalFetchCamel<T>(path: string, options?: RequestInit): Promise<T> {
  const raw = await portalFetch<unknown>(path, options);
  return transformKeysToCamelCase<T>(raw);
}

// ============================================================================
// Query Keys
// ============================================================================

export const portalKeys = {
  all: ['portal'] as const,
  dashboard: () => [...portalKeys.all, 'dashboard'] as const,
  content: (params?: Record<string, unknown>) => [...portalKeys.all, 'content', params] as const,
  contentDetail: (id: string) => [...portalKeys.all, 'content', id] as const,
  patterns: (params?: Record<string, unknown>) => [...portalKeys.all, 'patterns', params] as const,
  patternDetail: (id: string) => [...portalKeys.all, 'patterns', id] as const,
  patternTemporal: (id: string) => [...portalKeys.all, 'patterns', id, 'temporal'] as const,
  patternTriggers: (id: string) => [...portalKeys.all, 'patterns', id, 'triggers'] as const,
  patternImplications: (id: string) => [...portalKeys.all, 'patterns', id, 'implications'] as const,
  patternSuccession: (id: string) => [...portalKeys.all, 'patterns', id, 'succession'] as const,
  sourcesAll: () => [...portalKeys.all, 'sources'] as const,
  sources: (params?: Record<string, unknown>) => [...portalKeys.all, 'sources', params] as const,
  sourceProgress: (id: string) => [...portalKeys.all, 'source-progress', id] as const,
  team: () => [...portalKeys.all, 'team'] as const,
  joinRequests: () => [...portalKeys.all, 'join-requests'] as const,
  usage: () => [...portalKeys.all, 'usage'] as const,
  profile: () => [...portalKeys.all, 'profile'] as const,
  onboarding: () => [...portalKeys.all, 'onboarding'] as const,
  contentAnalysis: (id: string) => [...portalKeys.all, 'content', id, 'analysis'] as const,
  patternStats: () => [...portalKeys.all, 'pattern-stats'] as const,
  candidates: () => [...portalKeys.all, 'candidates'] as const,
  emergingEntities: () => [...portalKeys.all, 'emerging-entities'] as const,
  // CUR-465: portal-only diagnostic list of patterns citing org content but
  // hidden from the main feed by the domain-overlap filter.
  aboutYouPatterns: (limit?: number) => [...portalKeys.all, 'patterns', 'about-you', limit] as const,
  projectsAll: () => [...portalKeys.all, 'projects'] as const,
  projects: (params?: Record<string, unknown>) => [...portalKeys.all, 'projects', params] as const,
  projectDetail: (id: string) => [...portalKeys.all, 'projects', id] as const,
  projectSources: (id: string) => [...portalKeys.all, 'projects', id, 'sources'] as const,
  projectAvailableSources: (id: string, params?: Record<string, unknown>) => [...portalKeys.all, 'projects', id, 'available-sources', params] as const,
  projectRoster: (id: string) => [...portalKeys.all, 'projects', id, 'roster'] as const,
  projectPatterns: (id: string) => [...portalKeys.all, 'projects', id, 'patterns'] as const,
  projectMembers: (id: string) => [...portalKeys.all, 'projects', id, 'members'] as const,
  projectInboundAddress: (id: string) => [...portalKeys.all, 'projects', id, 'inbound-address'] as const,
  projectInboundLog: (id: string) => [...portalKeys.all, 'projects', id, 'inbound-log'] as const,
  inboundAddresses: () => [...portalKeys.all, 'inbound-addresses'] as const,
  webhooks: () => [...portalKeys.all, 'webhooks'] as const,
  billing: () => [...portalKeys.all, 'billing'] as const,
  playlistsAll: () => [...portalKeys.all, 'playlists'] as const,
  playlists: (params?: Record<string, unknown>) => [...portalKeys.all, 'playlists', params] as const,
  entities: (params?: Record<string, unknown>) => [...portalKeys.all, 'entities', params] as const,
  entityDetail: (id: string, params?: Record<string, unknown>) =>
    [...portalKeys.all, 'entities', id, params] as const,
};

// ============================================================================
// Cross-resource Types
// ============================================================================

export interface PortalOrganizationSummary {
  id: string;
  name: string;
  slug: string;
  plan: string;
  max_channels: number;
  max_users: number;
}
